import Big from 'big.js';
import { GetOptionPrice } from 'smg/customer-api/lineitems';
import { GetReadableSize, GetWidthHeightFromSize } from 'smg/lineitemtypesizes';
import * as models from 'smg/models';
import { CleanNumber } from 'smg/number';
import * as object from 'smg/object';
import { MaybePromise } from 'smg/promise';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly product: string;
}
declare const window: myWindow;

export declare interface Store {
    getStarted?: GetStartedStep;
    details?: DetailsStep;
    upload?: UploadStep;
    inDepthEnabled?: boolean;
    inDepth?: InDepthStep;
}

export declare interface GetStartedStep {
    name: string;
    email: string;
    phone: string;
    hasEventDate: boolean;
    eventDate: string;
    budgetLevel: 1 | 2 | 3 | 4;
    budgetDesc: string;
}

export declare interface DetailsStep {
    shape: string;
    customShape: string;

    color: string;
    type?: string;
    lineItemTypeID: models.LineItemTypeID;

    size: string;
    customWidth: number;
    customHeight: number;

    backing: string;
    attachment: string;

    qty: string;
    customQty: number;

    unitPrice: string;
    moldFee: string;
    total: string;
}

export declare interface UploadSide {
    uploadIDs: number[];
    notes: string;
}

export declare interface UploadStep {
    front?: UploadSide;
    back?: UploadSide;
}

export declare interface InDepthStep {
    type: string;
    lineItemTypeID: models.LineItemTypeID;
    plating: string;
    finish?: string;
    edge: string;
    attachment: string;
    accessory: string;
    feature: string;
    packaging: string;
    upgrade: string;
    location: string;

    unitPrice: string;
    moldFee: string;
    total: string;
}

export function IsGetStartedStep(obj: unknown): obj is GetStartedStep {
    if (!object.IsObject(obj)) {
        return false;
    }

    return typeof obj['name'] === 'string' &&
        typeof obj['email'] === 'string' &&
        typeof obj['phone'] === 'string' &&
        typeof obj['hasEventDate'] === 'boolean' &&
        typeof obj['eventDate'] === 'string' &&
        (obj['budgetLevel'] === 1 || obj['budgetLevel'] === 2 || obj['budgetLevel'] === 3 || obj['budgetLevel'] === 4) &&
        typeof obj['budgetDesc'] === 'string';
}

export function IsDetailsStep(obj: unknown): obj is DetailsStep {
    if (!object.IsObject(obj)) {
        return false;
    }

    return typeof obj['shape'] === 'string' &&
        typeof obj['customShape'] === 'string' &&
        typeof obj['color'] === 'string' &&
        typeof obj['size'] === 'string' &&
        typeof obj['customWidth'] === 'number' &&
        typeof obj['customHeight'] === 'number' &&
        typeof obj['backing'] === 'string' &&
        typeof obj['attachment'] === 'string' &&
        typeof obj['qty'] === 'string' &&
        typeof obj['customQty'] === 'number' &&
        typeof obj['unitPrice'] === 'string' &&
        typeof obj['moldFee'] === 'string' &&
        typeof obj['total'] === 'string';
}

export function IsUploadSide(obj: unknown): obj is UploadSide {
    if (!object.IsObject(obj)) {
        return false;
    }

    return Array.isArray(obj['uploadIDs']) &&
        obj['uploadIDs'].every(id => typeof id === 'number') &&
        typeof obj['notes'] === 'string';
}

export function IsUploadStep(obj: unknown): obj is UploadStep {
    if (!object.IsObject(obj)) {
        return false;
    }

    return (!obj['front'] || IsUploadSide(obj['front'])) &&
        (!obj['back'] || IsUploadSide(obj['back']));
}

export function IsInDepthStep(obj: unknown): obj is InDepthStep {
    if (!object.IsObject(obj)) {
        return false;
    }

    return typeof obj['type'] === 'string' &&
        typeof obj['lineItemTypeID'] === 'string' &&
        typeof obj['plating'] === 'string' &&
        typeof obj['attachment'] === 'string' &&
        typeof obj['accessory'] === 'string' &&
        typeof obj['packaging'] === 'string' &&
        typeof obj['upgrade'] === 'string' &&
        typeof obj['location'] === 'string' &&
        typeof obj['unitPrice'] === 'string' &&
        typeof obj['moldFee'] === 'string' &&
        typeof obj['total'] === 'string';
}

export function GetStoreKey(): string {
    if (window.location.href.includes('american-made')) {
        return 'freeQuoteDomestic';
    }
    return 'freeQuote';
}

export function Set(settings: Store) {
    window.sessionStorage.setItem(GetStoreKey(), JSON.stringify(settings));
    window.localStorage.setItem(GetStoreKey(), JSON.stringify(settings));
}

export function Get(): Store {
    try {
        const store = JSON.parse(window.sessionStorage.getItem(GetStoreKey()) || window.localStorage.getItem(GetStoreKey()) || '{}') as unknown;
        if (!object.IsObject(store)) {
            return {};
        }

        if (!IsGetStartedStep(store.getStarted)) {
            delete store.getStarted;
        }

        if (!IsDetailsStep(store.details)) {
            delete store.details;
        }

        if (!IsUploadStep(store.upload)) {
            delete store.upload;
        }

        return store;
    } catch (e) {
        console.error(e);
        return {};
    }
}

export function Clear() {
    window.sessionStorage.removeItem(GetStoreKey());
    window.localStorage.removeItem(GetStoreKey());
}

export function LatestCompleted(): keyof Store | undefined {
    const store = Get();
    if (store.inDepth && store.upload && store.details && store.getStarted) {
        return 'inDepth';
    } else if (store.upload && store.details && store.getStarted) {
        return 'upload';
    } else if (store.details && store.getStarted) {
        return 'details';
    } else if (store.getStarted) {
        return 'getStarted';
    }
}

export function Redirect() {
    const latestCompleted = LatestCompleted();
    if (!latestCompleted) {
        if (window.location.href.includes('american-made')) {
            window.location.href = Relref('/american-made-free-quote');
        } else {
            window.location.href = Relref('/free-quote');
        }
        return;
    }

    switch (latestCompleted) {
    case 'getStarted':
        if (window.location.href.includes('american-made')) {
            window.location.href = Relref('/american-made-free-quote/details');
        } else {
            window.location.href = Relref('/free-quote/details');
        }
        break;
    case 'details':
        if (window.location.href.includes('american-made')) {
            window.location.href = Relref('/american-made-free-quote/upload');
        } else {
            window.location.href = Relref('/free-quote/upload');
        }
        break;
    }
}

export interface DetailGetter {
    getDetailsColor?: () => string | undefined;
    getDetailsWidth?: () => number | undefined;
    getDetailsType?: () => string | undefined;
    getInDepthType?: () => string | undefined;
    getInDepthLineItemTypeID?: () => models.LineItemTypeID | undefined;

    getDetailsBacking?: () => string;
    getDetailsBackingPrice?: () => MaybePromise<Big>;

    getDetailsAttachment?: () => string | undefined;
    getDetailsAttachmentPrice?: () => MaybePromise<Big>;
}

export function Type(detailGetter?: DetailGetter): { name: string; lineItemTypeID: models.LineItemTypeID | undefined } {
    detailGetter ??= {};

    const settings = Get();
    detailGetter.getDetailsColor ??= () => settings.details?.color;
    detailGetter.getDetailsWidth ??= () => Width();
    detailGetter.getDetailsType ??= () => settings.details?.type;
    detailGetter.getInDepthType ??= () => settings.inDepth?.type;
    detailGetter.getInDepthLineItemTypeID ??= () => settings.details?.lineItemTypeID || settings.inDepth?.lineItemTypeID;

    const color = detailGetter.getDetailsColor();
    const width = detailGetter.getDetailsWidth();

    switch (window.product) {
    case 'Lapel Pin':
        switch (color) {
        case 'yes': {
            const inDepthType = detailGetter.getInDepthType();
            if (color === 'yes' && inDepthType) {
                return { name: inDepthType, lineItemTypeID: detailGetter.getInDepthLineItemTypeID() };
            }
            return { name: 'Soft Enamel', lineItemTypeID: models.LineItemTypeID.LINE_ITEM_TYPE_LAPEL_PIN_SOFT_ENAMEL };
        }
        default:
            return { name: 'Diestruck', lineItemTypeID: models.LineItemTypeID.LINE_ITEM_TYPE_LAPEL_PIN_DIE_STRUCK };
        }
    case 'Challenge Coin':
        return { name: detailGetter.getDetailsType() ?? detailGetter.getInDepthType() ?? detailGetter.getDetailsColor() ?? '', lineItemTypeID: detailGetter.getInDepthLineItemTypeID() };
    case 'Patch': {
        const inDepthType = detailGetter.getInDepthType();
        if (inDepthType) {
            return { name: inDepthType, lineItemTypeID: detailGetter.getInDepthLineItemTypeID() };
        }
        return { name: '100% Embroidery', lineItemTypeID: models.LineItemTypeID.LINE_ITEM_TYPE_PATCH_100_PERCENT_EMBROIDERY };
    }
    case 'Lanyard': {
        const inDepthType = detailGetter.getInDepthType();
        if (inDepthType) {
            return { name: inDepthType, lineItemTypeID: detailGetter.getInDepthLineItemTypeID() };
        }
        if (width === .5) {
            return { name: 'Imprinted Tube', lineItemTypeID: models.LineItemTypeID.LINE_ITEM_TYPE_LANYARD_IMPRINTED_TUBE };
        } else if (width === 1) {
            return { name: 'Woven Polyester', lineItemTypeID: models.LineItemTypeID.LINE_ITEM_TYPE_LANYARD_WOVEN_POLYESTER };
        }
        return { name: 'Imprinted Polyester', lineItemTypeID: models.LineItemTypeID.LINE_ITEM_TYPE_LANYARD_IMPRINTED_POLYESTER };
    }
    default:
        throw new Error('Unknown product');
    }
}

export function Size(): string | undefined {
    const settings = Get();

    if (settings.details?.size === 'custom') {
        if (!settings.details.customWidth || !settings.details.customHeight) {
            return;
        }
        return GetReadableSize(settings.details.customWidth, settings.details.customHeight, '');
    }

    return settings.details?.size;
}

export function Width(): number | undefined {
    const settings = Get();

    if (!settings.details?.size) {
        return;
    }

    if (settings.details.size === 'custom') {
        return settings.details.customWidth;
    }

    const { width } = GetWidthHeightFromSize(settings.details.size);
    return width;
}

export function Height(): number | undefined {
    const settings = Get();

    if (!settings.details?.size) {
        return;
    }

    if (settings.details.size === 'custom') {
        return settings.details.customHeight;
    }

    const { height } = GetWidthHeightFromSize(settings.details.size);
    return height;
}

export function Quantity(): number {
    const settings = Get();

    return settings.details?.qty === 'custom' ? settings.details.customQty || 0 : parseInt(CleanNumber(settings.details?.qty || '0'), 10);
}

export async function ChildrenTotalUnitPrice(detailGetter?: DetailGetter): Promise<Big> {
    const settings = Get();

    detailGetter ??= {};

    detailGetter.getDetailsBacking ??= () => settings.details?.backing ?? '';
    const getDetailsBacking = detailGetter.getDetailsBacking;
    detailGetter.getDetailsBackingPrice ??= () => {
        const backing = getDetailsBacking();
        if (!backing) {
            return Big(0);
        }
        return GetOptionPrice(window.product, 'Backing', backing);
    };

    detailGetter.getDetailsAttachment ??= () => settings.details?.attachment;
    const getDetailsAttachment = detailGetter.getDetailsAttachment;
    detailGetter.getDetailsAttachmentPrice ??= () => {
        const attachment = getDetailsAttachment();
        if (!attachment) {
            return Big(0);
        }
        return GetOptionPrice(window.product, 'Attachment', attachment);
    };

    let totalUnitPrice = Big(0);
    totalUnitPrice = totalUnitPrice.plus(await detailGetter.getDetailsBackingPrice());
    totalUnitPrice = totalUnitPrice.plus(await detailGetter.getDetailsAttachmentPrice());

    return totalUnitPrice;
}

export function Init() {
    const latestCompleted = LatestCompleted();
    const $stepGetStarted = document.querySelector('.free-quote-progress-step-get-started') as HTMLElement;
    const $stepDetails = document.querySelector('.free-quote-progress-step-details') as HTMLElement;
    const $stepUpload = document.querySelector('.free-quote-progress-step-upload') as HTMLElement;
    const $stepInDepth = document.querySelector('.free-quote-progress-step-in-depth') as HTMLElement;
    const $stepReview = document.querySelector('.free-quote-progress-step-review') as HTMLElement;

    if (latestCompleted) {
        switch (latestCompleted) {
        case 'inDepth':
            $stepInDepth.querySelector('.free-quote-progress-indicator')?.classList.add('complete');
            $stepInDepth.nextElementSibling?.classList.add('complete');
            $stepReview.querySelector('.free-quote-progress-indicator')?.classList.add('active');
            $stepReview.closest('.free-quote-progress-step')?.removeAttribute('disabled');
        // fallthrough
        case 'upload':
            $stepUpload.querySelector('.free-quote-progress-indicator')?.classList.add('complete');
            $stepUpload.nextElementSibling?.classList.add('complete');
            $stepInDepth.querySelector('.free-quote-progress-indicator')?.classList.add('active');
            $stepInDepth.closest('.free-quote-progress-step')?.removeAttribute('disabled');
            $stepReview.closest('.free-quote-progress-step')?.removeAttribute('disabled');
        // fallthrough
        case 'details':
            $stepDetails.querySelector('.free-quote-progress-indicator')?.classList.add('complete');
            $stepDetails.nextElementSibling?.classList.add('complete');
            $stepUpload.querySelector('.free-quote-progress-indicator')?.classList.add('active');
            $stepUpload.closest('.free-quote-progress-step')?.removeAttribute('disabled');
        // fallthrough
        case 'getStarted':
            $stepGetStarted.querySelector('.free-quote-progress-indicator')?.classList.add('complete');
            $stepGetStarted.nextElementSibling?.classList.add('complete');
            $stepDetails.querySelector('.free-quote-progress-indicator')?.classList.add('active');
            $stepDetails.closest('.free-quote-progress-step')?.removeAttribute('disabled');
        }
    }

    if (latestCompleted === 'inDepth' || Get().inDepthEnabled) {
        $stepInDepth.classList.remove('d-none');
        $stepInDepth.nextElementSibling?.classList.remove('d-none');
    }
}
